import React from 'react';
import PropTypes from 'prop-types';
import { LuBuilding2, LuBuilding, LuDownload, LuRefreshCw, LuTableProperties, LuUsers, LuUserRound } from 'react-icons/lu';
import PageHero from '../../../components/data/PageHero';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import useSmoText from '../smoText';

/** Page title with the page actions (shown once an SMO with workers is open). */
export const SmoHeader = ({ showActions, canManualEntry, loadingWorkers, onManualEntry, onRefresh, onDownload }) => {
  const { label } = useSmoText();
  return (
    <PageHero
      icon={LuBuilding2}
      gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30"
      title={label('title')}
      subtitle={label('breadcrumb')}
    >
      {showActions && (
        <>
          {canManualEntry && (
            <Button variant="primary" onClick={onManualEntry}>
              <LuTableProperties aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('header.manualEntry')}
            </Button>
          )}
          <Button onClick={onRefresh} disabled={loadingWorkers} title={label('header.refreshHint')}>
            <LuRefreshCw aria-hidden="true" className={`tw-h-4 tw-w-4 ${loadingWorkers ? 'tw-animate-spin' : ''}`} />
            {loadingWorkers ? label('header.refreshing') : label('header.refresh')}
          </Button>
          <Button variant="success" onClick={onDownload} title={label('header.downloadHint')}>
            <LuDownload aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('header.download')}
          </Button>
        </>
      )}
    </PageHero>
  );
};

SmoHeader.propTypes = {
  showActions: PropTypes.bool,
  canManualEntry: PropTypes.bool,
  loadingWorkers: PropTypes.bool,
  onManualEntry: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
};

export const Stat = ({ icon: Icon, gradient, label, value, note }) => (
  <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3 tw-rounded-[1.4rem] tw-border tw-border-white/80 tw-bg-white/70 tw-p-4 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_12px_32px_-24px_rgba(15,23,42,0.4)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_12px_32px_-24px_rgba(0,0,0,0.9)]">
    <GlassIcon gradient={gradient} size="md">
      <Icon aria-hidden="true" className="tw-h-6 tw-w-6" />
    </GlassIcon>
    <div className="tw-min-w-0">
      <p className="tw-truncate tw-text-xs tw-font-medium tw-text-slate-500 sm:tw-text-sm dark:tw-text-slate-400">{label}</p>
      <p className="tw-mt-0.5 tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-2 tw-text-2xl tw-font-semibold tw-leading-none tw-tracking-tight tw-text-slate-900 tw-tabular-nums dark:tw-text-white">
        {value}
        {note && <span className="tw-text-xs tw-font-medium tw-tracking-normal tw-text-slate-500 dark:tw-text-slate-400">{note}</span>}
      </p>
    </div>
  </div>
);

Stat.propTypes = {
  icon: PropTypes.elementType.isRequired,
  gradient: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  note: PropTypes.node,
};

/** Head-count cards: all SMOs, then the open SMO's centres and workers. */
export const SmoStats = ({ smoCount, selectedSMO, workerCount }) => {
  const { label } = useSmoText();
  let centerNote = null;
  if (selectedSMO?.centers) {
    const primerCount = selectedSMO.centers.filter((c) => c.isPrimerSmo).length;
    const backupCount = selectedSMO.centers.filter((c) => !c.isPrimerSmo).length;
    if (primerCount > 0 && backupCount > 0) centerNote = label('stats.centerSplit', { primer: primerCount, backup: backupCount });
  }
  return (
    <div className={`tw-grid tw-gap-3 sm:tw-gap-4 ${selectedSMO ? 'sm:tw-grid-cols-3' : ''}`}>
      <Stat icon={LuUserRound} gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30" label={label('stats.totalSmo')} value={smoCount} />
      {selectedSMO && (
        <Stat icon={LuBuilding} gradient="tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30" label={label('stats.centers')} value={selectedSMO.centerCount} note={centerNote} />
      )}
      {selectedSMO && (
        <Stat icon={LuUsers} gradient="tw-from-rose-400 tw-to-red-600 tw-shadow-rose-500/30" label={label('stats.workers')} value={workerCount} />
      )}
    </div>
  );
};

SmoStats.propTypes = {
  smoCount: PropTypes.number.isRequired,
  selectedSMO: PropTypes.object,
  workerCount: PropTypes.number.isRequired,
};
